// ExploreTab — the 4th bottom-nav tab.
//
// Surfaces insights from the platform's sermon data: what's trending, a Bible
// coverage heat map, and church discovery. Study Plans is intentionally left
// for a later iteration (see STUDY-PLANS-NOTES.md).
//
// Data comes from SECURITY DEFINER RPCs in supabase-schema-explore.sql
// (explore_trending / explore_book_coverage / explore_stats /
// explore_recent_sermons) and church_stats() from the networks migration.
// Everything degrades gracefully if those RPCs aren't present yet.
//
// Props:
//   churchIds   — array of church ids the user follows (home + extras). Used as
//                 the "Churches I follow" scope; pass [] / null for none.
//   churches    — full church list (for id → name/logo lookups + Churches tab).
//   activeChurchId, sermonNoteChurchIds, setSermonNoteChurchIds — follow state.
//   user        — { id, email } for the request-a-church form.
//   onOpenVerse(book, chapter, verse) — navigate the Bible reader to a verse.

import { useEffect, useMemo, useState, useRef } from "react";
import { Search, TrendingUp, ChevronRight } from "lucide-react";
import { supabase } from "../supabase";
import { behindStyle, prefersReducedMotion } from "./StackedSheet";

const GOLD = "var(--gold)";

// 66 books in canonical order with chapter counts (block sizing for the map).
const BOOK_CHAPTERS = {
  "Genesis":50,"Exodus":40,"Leviticus":27,"Numbers":36,"Deuteronomy":34,"Joshua":24,
  "Judges":21,"Ruth":4,"1 Samuel":31,"2 Samuel":24,"1 Kings":22,"2 Kings":25,
  "1 Chronicles":29,"2 Chronicles":36,"Ezra":10,"Nehemiah":13,"Esther":10,"Job":42,
  "Psalms":150,"Proverbs":31,"Ecclesiastes":12,"Song of Solomon":8,"Isaiah":66,
  "Jeremiah":52,"Lamentations":5,"Ezekiel":48,"Daniel":12,"Hosea":14,"Joel":3,
  "Amos":9,"Obadiah":1,"Jonah":4,"Micah":7,"Nahum":3,"Habakkuk":3,"Zephaniah":3,
  "Haggai":2,"Zechariah":14,"Malachi":4,
  "Matthew":28,"Mark":16,"Luke":24,"John":21,"Acts":28,"Romans":16,"1 Corinthians":16,
  "2 Corinthians":13,"Galatians":6,"Ephesians":6,"Philippians":4,"Colossians":4,
  "1 Thessalonians":5,"2 Thessalonians":3,"1 Timothy":6,"2 Timothy":4,"Titus":3,
  "Philemon":1,"Hebrews":13,"James":5,"1 Peter":5,"2 Peter":3,"1 John":5,"2 John":1,
  "3 John":1,"Jude":1,"Revelation":22,
};
const BOOK_ORDER = Object.keys(BOOK_CHAPTERS);
const TOTAL_VERSES = 31102;

// Fixed, unique abbreviation per book (docs/FIX-BIBLE-MAP.md) — replaces the
// old length-based truncation, which collided on Philippians/Philemon,
// Thessalonians/Timothy and Chronicles/Corinthians.
const BOOK_ABBR = {
  "Genesis":"Gen","Exodus":"Exo","Leviticus":"Lev","Numbers":"Num","Deuteronomy":"Deu","Joshua":"Jos",
  "Judges":"Jdg","Ruth":"Rut","1 Samuel":"1Sa","2 Samuel":"2Sa","1 Kings":"1Ki","2 Kings":"2Ki",
  "1 Chronicles":"1Ch","2 Chronicles":"2Ch","Ezra":"Ezr","Nehemiah":"Neh","Esther":"Est","Job":"Job",
  "Psalms":"Psa","Proverbs":"Pro","Ecclesiastes":"Ecc","Song of Solomon":"Song","Isaiah":"Isa",
  "Jeremiah":"Jer","Lamentations":"Lam","Ezekiel":"Eze","Daniel":"Dan","Hosea":"Hos","Joel":"Joel",
  "Amos":"Amos","Obadiah":"Oba","Jonah":"Jon","Micah":"Mic","Nahum":"Nah","Habakkuk":"Hab","Zephaniah":"Zep",
  "Haggai":"Hag","Zechariah":"Zec","Malachi":"Mal",
  "Matthew":"Mat","Mark":"Mark","Luke":"Luke","John":"John","Acts":"Acts","Romans":"Rom","1 Corinthians":"1Co",
  "2 Corinthians":"2Co","Galatians":"Gal","Ephesians":"Eph","Philippians":"Php","Colossians":"Col",
  "1 Thessalonians":"1Th","2 Thessalonians":"2Th","1 Timothy":"1Ti","2 Timothy":"2Ti","Titus":"Tit",
  "Philemon":"Phm","Hebrews":"Heb","James":"Jas","1 Peter":"1Pe","2 Peter":"2Pe","1 John":"1Jn","2 John":"2Jn",
  "3 John":"3Jn","Jude":"Jude","Revelation":"Rev",
};

// 6-step lavender heat scale (docs/FIX-BIBLE-MAP.md) — none/light→dark, never red.
const HEAT_STEPS = [
  { bg: "#F1EDE6", ink: "#9A9086" },
  { bg: "#E9E3FA", ink: "#2E2552" },
  { bg: "#CFC4F2", ink: "#2E2552" },
  { bg: "#A998E2", ink: "#2E2552" },
  { bg: "#7C6BC4", ink: "#FFFFFF" },
  { bg: "#4B3D8F", ink: "#FFFFFF" },
];
function heatStep(n) {
  if (!n)       return HEAT_STEPS[0];
  if (n <= 5)   return HEAT_STEPS[1];
  if (n <= 20)  return HEAT_STEPS[2];
  if (n <= 50)  return HEAT_STEPS[3];
  if (n <= 100) return HEAT_STEPS[4];
  return HEAT_STEPS[5];
}
function initialOf(s) {
  const t = String(s || "").trim();
  return t ? t[0].toUpperCase() : "?";
}
// Two-letter initials for the church circles (e.g. "New Life Church" -> "NL").
export function initialsOf(s) {
  const words = String(s || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// ── Small shared bits ───────────────────────────────────────────────────────
function SubTabs({ value, onChange }) {
  const tabs = [
    { id: "trending", label: "Trending" },
    { id: "map",      label: "Bible Map" },
    { id: "churches", label: "Discover" },
  ];
  return (
    <div style={{display:"flex", gap:4, borderBottom:"1px solid var(--border)", marginBottom:16}}>
      {tabs.map(t => {
        const on = value === t.id;
        return (
          <button key={t.id} type="button" onClick={()=>onChange(t.id)}
            style={{
              flex:1, background:"none", border:"none", cursor:"pointer",
              padding:"10px 4px", fontSize:12.5, fontWeight:on?800:600,
              letterSpacing:"0.04em", textTransform:"uppercase",
              color:on?"var(--gold-ink)":"var(--stone)",
              borderBottom:`2px solid ${on?GOLD:"transparent"}`,
              fontFamily:"'Plus Jakarta Sans',sans-serif",
            }}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// activeBg/activeColor let a screen use its own accent for the active chip —
// e.g. the Sermons tab uses lavender (its palette meaning) instead of gold.
export function ScopeToggle({ scope, onChange, options, activeBg = GOLD, activeColor = "var(--ink)" }) {
  const opts = options || [["followed","Churches I follow"],["all","All churches"]];
  return (
    <div style={{display:"flex", flexWrap:"wrap", gap:8, marginBottom:16}}>
      {opts.map(([id,label]) => {
        const on = scope === id;
        return (
          <button key={id} type="button" onClick={()=>onChange(id)}
            style={{
              background:on?activeBg:"var(--white)", color:on?activeColor:"var(--stone)",
              border:"none", borderRadius:999, padding:"9px 16px", fontSize:13, fontWeight:on?700:600,
              cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:"nowrap",
            }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

// Persisted "which of my followed churches am I currently viewing" filter —
// deliberately separate from sermonNoteChurchIds (the actual follow
// relationship, changed via Find churches). Stores the *deselected* ids so a
// newly-followed church defaults to selected without needing a migration
// each time the follow list grows. Shared by the Sermons tab and Bible Map
// (docs/SERMONS-SPEC.md §2, docs/FIX-BIBLE-MAP.md) so picking churches in one
// place is reflected in the other.
function churchSelectionKey(userId) { return `nlc.churchCircleDeselected:${userId}`; }
export function useChurchCircleSelection(userId, poolIds) {
  const [deselected, setDeselected] = useState(() => {
    if (typeof window === "undefined" || !userId) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(churchSelectionKey(userId)) || "[]")); }
    catch { return new Set(); }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !userId) return;
    try { localStorage.setItem(churchSelectionKey(userId), JSON.stringify([...deselected])); } catch {}
  }, [deselected, userId]);

  const poolKey = (poolIds || []).join(",");
  const selectedIds = useMemo(() => (poolIds || []).filter(id => !deselected.has(id)), [poolKey, deselected]); // eslint-disable-line react-hooks/exhaustive-deps

  // The home church can be deselected and reselected like any other church
  // — it's no longer special-cased here. Callers may still pass a second
  // `homeChurchId` arg (harmless, just unused) for backwards compatibility
  // with existing call sites.
  function toggle(id) {
    setDeselected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAll() { setDeselected(new Set()); }
  function clear() { setDeselected(new Set(poolIds || [])); }

  return { selectedIds, toggle, selectAll, clear };
}

export const CIRCLE_PASTELS = [
  { bg: "var(--lavender)", ink: "var(--lavender-ink)" },
  { bg: "var(--sage)",     ink: "var(--sage-ink)" },
  { bg: "var(--peach)",    ink: "var(--peach-ink)" },
  { bg: "var(--sky)",      ink: "var(--sky-ink)" },
  { bg: "var(--blush)",    ink: "var(--blush-ink)" },
];

// Shows the church's logo, falling back to initials if there's no logo OR
// the image fails to load (404/broken URL) — a plain `url ? <img/> : text`
// ternary leaves the circle empty on a load failure, since onError can only
// hide the broken <img>, not swap in the text sibling that was never
// rendered. This is almost certainly why a church with a stale/broken
// logo_url showed an empty circle (docs/FIX-SERMONS-POLISH.md §1).
export function CircleLogoOrInitials({ url, name }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return initialsOf(name);
  return <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setFailed(true)}/>;
}

// Multi-select church picker — docs/designs/SermonsChurches.dc.html /
// BibleMap.dc.html. `pool` is the ordered list of selectable churches, home
// first. Every circle toggles independently; selected shows a small black
// checkmark badge, deselected just has no badge (full colour either way).
export function ChurchCircles({ pool, homeChurchId, selectedIds, onToggle, onSelectAll, onClear, onFindChurches }) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const total = pool.length;
  const shown = selectedSet.size;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, color: "var(--stone)" }}>
          Showing <b style={{ color: "var(--ink)" }}>{shown} of {total}</b> churches
        </span>
        <span style={{ display: "flex", gap: 14, fontSize: 12.5, fontWeight: 700 }}>
          <button type="button" onClick={onSelectAll} style={{ background: "none", border: "none", color: "var(--gold-ink)", cursor: "pointer", padding: 0, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Select all</button>
          <button type="button" onClick={onClear} style={{ background: "none", border: "none", color: "var(--stone)", cursor: "pointer", padding: 0, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Clear</button>
        </span>
      </div>
      <div className="church-circles-hscroll" style={{ display: "flex", gap: 10, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 2, marginRight: -22 }}>
        <style>{".church-circles-hscroll{scrollbar-width:none}.church-circles-hscroll::-webkit-scrollbar{display:none}"}</style>
        {pool.map((c, i) => {
          const isHome = c.id === homeChurchId;
          const on = selectedSet.has(c.id);
          const pastel = CIRCLE_PASTELS[i % CIRCLE_PASTELS.length];
          // The badge sits on an UNCLIPPED outer wrapper — the circle itself
          // needs overflow:hidden to crop a logo image, which would otherwise
          // clip the badge too, since it's positioned partly outside the
          // circle's own box (docs/FIX-SERMONS-POLISH.md §1: this clipping,
          // not just sizing, was why the ✓ showed as "a tiny dark blob").
          const circle = (
            <span style={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
              <span style={{ width: 52, height: 52, borderRadius: 26, overflow: "hidden", background: isHome ? "var(--lavender)" : pastel.bg, color: isHome ? "var(--lavender-ink)" : pastel.ink, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800 }}>
                <CircleLogoOrInitials url={c.logo_url} name={c.name}/>
              </span>
              {on && (
                <span style={{ position: "absolute", right: -4, bottom: -4, width: 22, height: 22, borderRadius: 11, background: "#1F1B16", border: `2px solid ${isHome ? "var(--white)" : "var(--cream)"}`, boxSizing: "content-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>
                </span>
              )}
            </span>
          );
          return (
            <button key={c.id} type="button" onClick={() => onToggle(c.id, homeChurchId)}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: 60, flexShrink: 0, background: isHome ? "var(--white)" : "none", borderRadius: isHome ? 20 : 0, padding: isHome ? "8px 8px 7px" : 0, border: "none", cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {circle}
              {isHome ? (
                <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 800, color: "var(--gold-ink)" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 11l9-7 9 7v9H3z"/></svg>
                  {c.name}
                </span>
              ) : (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: on ? "var(--ink)" : "var(--stone)", textAlign: "center", lineHeight: 1.2, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{c.name}</span>
              )}
            </button>
          );
        })}
        <button type="button" onClick={onFindChurches}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: 60, flexShrink: 0, background: "none", border: "none", cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <span style={{ width: 52, height: 52, borderRadius: 26, border: "2px dashed var(--border-strong)", color: "var(--gold-ink)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 600, boxSizing: "border-box" }}>+</span>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--stone)" }}>Find</span>
        </button>
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div style={{display:"inline-flex", background:"var(--white)", border:"1px solid var(--border-strong)", borderRadius:999, padding:3}}>
      {options.map(([id, label]) => {
        const on = value === id;
        return (
          <button key={id} type="button" onClick={()=>onChange(id)}
            style={{
              background:on?GOLD:"transparent", color:on?"var(--ink)":"var(--stone)",
              border:"none", borderRadius:999, padding:"6px 12px", fontSize:12, fontWeight:700,
              cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif",
            }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ChurchLogo({ url, name, size=28 }) {
  const [failed, setFailed] = useState(false);
  return (
    <div style={{
      width:size, height:size, borderRadius:size/4, overflow:"hidden", flexShrink:0,
      background:"rgba(232,200,122,0.12)", border:"1px solid rgba(232,200,122,0.25)",
      display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:size*0.42, fontWeight:700, color:"var(--gold-ink)",
    }}>
      {url && !failed
        ? <img src={url} alt="" loading="lazy" referrerPolicy="no-referrer"
            style={{width:"100%",height:"100%",objectFit:"cover"}}
            onError={()=>setFailed(true)}/>
        : initialOf(name)}
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function ExploreTab({
  churchIds = [], churches = [],
  activeChurchId, sermonNoteChurchIds = [], setSermonNoteChurchIds = () => {},
  user, onOpenVerse, onOpenChurch,
  sub: subProp, setSub: setSubProp,
}) {
  const [subState, setSubState] = useState("trending");
  const sub = subProp != null ? subProp : subState;
  const setSub = setSubProp || setSubState;

  // id → church (name, logo, location, network) for quick lookups.
  const churchById = useMemo(() => {
    const m = new Map();
    for (const c of churches || []) m.set(c.id, c);
    return m;
  }, [churches]);

  // Effective followed set (home + extras), deduped.
  const followedIds = useMemo(() => {
    const s = new Set((churchIds || []).filter(Boolean));
    if (activeChurchId) s.add(activeChurchId);
    return s;
  }, [churchIds, activeChurchId]);

  return (
    <div style={{padding:"4px 16px 32px", maxWidth:560, margin:"0 auto"}}>
      <SubTabs value={sub} onChange={setSub}/>
      {sub === "trending" && (
        <TrendingView followedIds={followedIds} homeChurchId={activeChurchId} churchById={churchById} onOpenVerse={onOpenVerse}/>
      )}
      {sub === "map" && (
        <BibleMapView followedIds={followedIds} homeChurchId={activeChurchId} churchById={churchById} onOpenVerse={onOpenVerse}/>
      )}
      {sub === "churches" && (
        <ChurchesView
          churchById={churchById}
          activeChurchId={activeChurchId}
          sermonNoteChurchIds={sermonNoteChurchIds}
          setSermonNoteChurchIds={setSermonNoteChurchIds}
          user={user}
          onOpenChurch={onOpenChurch}/>
      )}
    </div>
  );
}

// ── 3a. TRENDING ────────────────────────────────────────────────────────────
// externalScope: when provided (e.g. mounted inline on the Sermons tab, which
// already has its own page-level chip bar), this view uses that scope instead
// of managing its own and hides its internal ScopeToggle — avoids showing two
// redundant scope selectors on one screen. When omitted (ExploreTab's own
// legacy sub-tab shell), it manages scope itself exactly as before.
// churchIds: when provided (the new multi-select circles, docs/SERMONS-SPEC.md
// §5), used directly as the RPC scope and the internal scope buttons are
// skipped entirely — supersedes the older externalScope string for that case.
// display="pills": the Sermons tab's "Most preached" section — adaptive
// heading/subtitle, an always-visible Month/Year/All time toggle (with a
// fewer-than-3-results auto-widen), and count pills on each chip.
export function TrendingView({ followedIds, homeChurchId, churchById, onOpenVerse, externalScope, externalPeriod, display = "cards", churchIds, heading, subtitle, countMode = "sermons", defaultPeriod }) {
  const [internalScope, setInternalScope] = useState(homeChurchId ? "home" : "followed");
  const scope = externalScope != null ? externalScope : internalScope;
  const setScope = setInternalScope;
  const [internalPeriod, setInternalPeriod] = useState(defaultPeriod || "alltime");   // month | year | alltime
  const period = externalPeriod != null ? externalPeriod : internalPeriod;
  const setPeriod = setInternalPeriod;
  const [rows, setRows] = useState(null);   // null = loading
  const [error, setError] = useState("");

  // Reset to the caller's preferred default whenever the selection shape
  // changes (e.g. going from "just home" to "several churches").
  useEffect(() => { if (display === "pills" && defaultPeriod) setInternalPeriod(defaultPeriod); }, [defaultPeriod, display]);

  const periodDays = period === "month" ? 30 : period === "year" ? 365 : null;
  const periodLabel = period === "month" ? "this month" : period === "year" ? "this year" : "of all time";
  const scopeOptions = [
    ...(homeChurchId ? [["home", "Home church"]] : []),
    ["followed", "Churches I follow"],
    ["all", "All churches"],
  ];
  const scopeIds = (s) => s === "all" ? null : s === "home" ? (homeChurchId ? [homeChurchId] : []) : [...followedIds];
  const effectiveIds = churchIds != null ? churchIds : scopeIds(scope);
  const effectiveKey = (churchIds || []).join(",");

  useEffect(() => {
    let cancelled = false;
    setRows(null); setError("");
    const ids = effectiveIds;
    if (ids && ids.length === 0) { setRows([]); return; }
    supabase.rpc("explore_trending", { p_church_ids: ids, p_days: periodDays, p_limit: 10 })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setError(error.message); setRows([]); return; }
        setRows(Array.isArray(data) ? data : []);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, effectiveKey, followedIds, homeChurchId, periodDays]);

  // Fewer than 3 results at the current range → widen to the next range up
  // and move the toggle to match (docs/SERMONS-SPEC.md §5).
  useEffect(() => {
    if (display !== "pills" || rows == null) return;
    if (rows.length >= 3) return;
    if (period === "month") setInternalPeriod("year");
    else if (period === "year") setInternalPeriod("alltime");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, period, display]);

  // Callers with a fixed descriptive subtitle (e.g. single-church "Verses
  // your church comes back to") pass it explicitly; omitting it falls back
  // to a plain period label so the text always matches the Month/Year/All
  // time toggle just below it, instead of going stale (docs/FIX-SERMONS-POLISH.md §5).
  const resolvedSubtitle = subtitle != null ? subtitle
    : (display === "pills" ? (period === "month" ? "This month" : period === "year" ? "This year" : "All time") : null);

  if (display === "pills") {
    return (
      <div>
        <h2 style={{fontSize:18, fontWeight:700, color:"var(--ink)", margin:"0 0 2px"}}>{heading}</h2>
        {resolvedSubtitle && <p style={{fontSize:12.5, color:"var(--stone)", margin:"0 0 10px", lineHeight:1.4}}>{resolvedSubtitle}</p>}
        <div style={{display:"flex", gap:16, marginBottom:12}}>
          {[["month","Month"],["year","Year"],["alltime","All time"]].map(([id,label]) => {
            const on = period === id;
            return (
              <button key={id} type="button" onClick={()=>setInternalPeriod(id)}
                style={{
                  background:"none", border:"none", padding:"0 0 6px", cursor:"pointer",
                  fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13,
                  fontWeight:on?800:600, color:on?"var(--ink)":"var(--stone)",
                  borderBottom:on?`2px solid ${GOLD}`:"2px solid transparent",
                }}>
                {label}
              </button>
            );
          })}
        </div>
        {rows === null ? <Loading/> : error ? (
          <Empty>Couldn't load most-preached verses.</Empty>
        ) : rows.length === 0 ? (
          <Empty>No sermons found {period === "alltime" ? "yet" : `for ${periodLabel}`}.</Empty>
        ) : (
          <div style={{display:"flex", flexWrap:"wrap", gap:8}}>
            {rows.slice(0, 6).map((r, i) => {
              const ref = `${r.book} ${r.chapter}:${r.verse}`;
              const countText = countMode === "churches"
                ? `${r.church_count} ${r.church_count === 1 ? "church" : "churches"}`
                : countMode === "refs"
                ? `${r.ref_count}`
                : `${r.sermon_count}`;
              return (
                <button key={`${ref}-${i}`} type="button"
                  onClick={()=>onOpenVerse?.(r.book, r.chapter, r.verse, "all")}
                  style={{
                    display:"inline-flex", alignItems:"center", gap:7, border:"none", borderRadius:999, padding:"8px 8px 8px 13px",
                    background:"#CFE0F2", color:"#253A50", fontSize:13, fontWeight:700,
                    cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif",
                  }}>
                  {ref}
                  <span style={{background:"var(--white)", color:"#253A50", borderRadius:999, padding:"2px 7px", fontSize:11, fontWeight:800}}>{countText}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {externalPeriod == null && (
        <>
          <h2 style={{fontSize:22, fontWeight:800, color:"var(--ink)", margin:"0 0 4px"}}>Trending {periodLabel}</h2>
          <p style={{fontSize:13, color:"var(--stone)", margin:"0 0 14px", lineHeight:1.5}}>
            Verses preached on most {period === "alltime" ? "across all time" : `in ${periodLabel}`} — by the sermon date.
          </p>
        </>
      )}
      <div style={{display:"flex", flexWrap:"wrap", gap:8, alignItems:"center"}}>
        {externalScope == null && <ScopeToggle scope={scope} onChange={setScope} options={scopeOptions}/>}
        {externalPeriod == null && (
          <Segmented
            value={period}
            onChange={setPeriod}
            options={[["month","This month"],["year","This year"],["alltime","All time"]]}/>
        )}
      </div>

      {display === "chips" ? (
        // Compact preview (docs/designs/SermonsSeries.html) — a plain wrap of
        // sky verse chips, top 5-6, no cards and no empty-state placeholder;
        // the rich card list (with church avatars/sermon counts, below) is
        // reserved for the full "See all" overlay.
        rows && rows.length > 0 && (
          <div style={{display:"flex", flexWrap:"wrap", gap:8}}>
            {rows.slice(0, 6).map((r, i) => {
              const ref = `${r.book} ${r.chapter}:${r.verse}`;
              return (
                <button key={`${ref}-${i}`} type="button"
                  onClick={()=>onOpenVerse?.(r.book, r.chapter, r.verse, scope)}
                  style={{
                    border:"none", borderRadius:999, padding:"8px 13px",
                    background:"#CFE0F2", color:"#253A50", fontSize:13, fontWeight:700,
                    cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif",
                  }}>
                  {ref}
                </button>
              );
            })}
          </div>
        )
      ) : (
        <>
      <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"var(--stone)", margin:"4px 0 10px"}}>
        Most preached verses
      </div>

      {rows === null ? (
        <Loading/>
      ) : error ? (
        <Empty>Couldn't load trending verses.</Empty>
      ) : rows.length === 0 ? (
        <Empty>
          {scope === "followed"
            ? "Follow some churches to see what they're preaching on."
            : `No sermons found ${period === "alltime" ? "yet" : "for " + periodLabel}.`}
        </Empty>
      ) : (
        <div style={{display:"flex", flexDirection:"column", gap:8}}>
          {rows.map((r, i) => {
            const ref = `${r.book} ${r.chapter}:${r.verse}`;
            const avatars = (r.church_ids || []).slice(0, 5);
            return (
              <button key={`${ref}-${i}`} type="button"
                onClick={()=>onOpenVerse?.(r.book, r.chapter, r.verse, scope)}
                style={{
                  display:"flex", alignItems:"center", gap:12, width:"100%", textAlign:"left",
                  background:"var(--white)", border:"1px solid var(--border)",
                  borderLeft:`2px solid ${GOLD}`, borderRadius:10, padding:"12px 14px", cursor:"pointer",
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                }}>
                <TrendingUp size={16} color={GOLD} style={{flexShrink:0}}/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:14.5, fontWeight:700, color:"var(--ink)"}}>{ref}</div>
                  <div style={{display:"flex", alignItems:"center", gap:-6, marginTop:6}}>
                    {avatars.map((cid, j) => {
                      const c = churchById.get(cid);
                      return (
                        <span key={cid} style={{marginLeft:j===0?0:-6, border:"1.5px solid var(--white)", borderRadius:8}}>
                          <ChurchLogo url={c?.logo_url} name={c?.name} size={22}/>
                        </span>
                      );
                    })}
                    {r.church_count > avatars.length && (
                      <span style={{marginLeft:6, fontSize:11, color:"var(--stone)"}}>+{r.church_count - avatars.length}</span>
                    )}
                  </div>
                </div>
                <div style={{flexShrink:0, display:"flex", alignItems:"center", gap:8}}>
                  <span style={{fontSize:11, fontWeight:700, color:"var(--gold-ink)", background:"rgba(232,200,122,0.12)", border:"1px solid rgba(232,200,122,0.25)", borderRadius:8, padding:"3px 8px", whiteSpace:"nowrap"}}>
                    {r.sermon_count} sermon{Number(r.sermon_count) === 1 ? "" : "s"}
                  </span>
                  <span style={{width:8, height:8, borderRadius:"50%", background:"#DC2626", flexShrink:0}}/>
                </div>
              </button>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}

// ── 3b. BIBLE MAP ───────────────────────────────────────────────────────────

// Equal-size 7-column book grid (docs/designs/BibleMap.dc.html) — fixed tile
// size for phone readability, not proportional to chapter count. `mini`
// renders a tiny label-less preview (the Leaders-dashboard "Preaching
// insights" card teaser).
function HeatGrid({ coverage, mini, onBook, openBook, books = BOOK_ORDER }) {
  return (
    <div style={{display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:mini?2:5, marginBottom:mini?0:8}}>
      {books.map(b => {
        const n = coverage?.[b]?.sermon || 0;
        const step = heatStep(n);
        return (
          <button key={b} type="button" title={`${b} — ${n} sermon${n===1?"":"s"}`}
            onClick={onBook ? ()=>onBook(b) : undefined}
            style={{
              height: mini?8:40, borderRadius: mini?2:10, cursor: onBook?"pointer":"default",
              background: step.bg, color: step.ink, transition:"background-color 300ms ease",
              border: openBook === b ? `1.5px solid ${GOLD}` : "none",
              fontSize: mini?0:11.5, fontWeight:800, overflow:"hidden", whiteSpace:"nowrap",
              padding:0, fontFamily:"'Plus Jakarta Sans',sans-serif",
            }}>
            {mini ? "" : (BOOK_ABBR[b] || b)}
          </button>
        );
      })}
    </div>
  );
}
// Same fixed grid for a book's chapters (docs/designs/BibleMapBook.dc.html).
function ChapterGrid({ chapterCoverage, chapterCount, onChapter, mostPreachedChapter }) {
  const chapters = Array.from({ length: chapterCount }, (_, i) => i + 1);
  return (
    <div style={{display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:5, marginBottom:8}}>
      {chapters.map(ch => {
        const n = chapterCoverage?.[ch] || 0;
        const step = heatStep(n);
        return (
          <button key={ch} type="button" title={`Chapter ${ch} — ${n} sermon${n===1?"":"s"}`}
            onClick={onChapter ? ()=>onChapter(ch) : undefined}
            style={{
              height:40, borderRadius:10, cursor: onChapter?"pointer":"default",
              background: step.bg, color: step.ink,
              border: mostPreachedChapter === ch ? `1.5px solid ${GOLD}` : "none",
              fontSize:13, fontWeight:800, fontFamily:"'Plus Jakarta Sans',sans-serif",
            }}>
            {ch}
          </button>
        );
      })}
    </div>
  );
}
function HeatLegend() {
  return (
    <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:18, fontSize:10.5, color:"var(--stone)"}}>
      <span>Not preached</span>
      {HEAT_STEPS.map((s,i) => <span key={i} style={{width:16,height:10,borderRadius:3,background:s.bg,display:"inline-block"}}/>)}
      <span>Most preached</span>
    </div>
  );
}

function pillStyle(on) {
  return {
    flexShrink:0, background:on?GOLD:"var(--white)", color:on?"var(--ink)":"var(--stone)",
    border:on?"none":"1px solid var(--border-strong)", borderRadius:999, padding:"7px 13px",
    fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", fontFamily:"'Plus Jakarta Sans',sans-serif",
  };
}

const OT_BOOK_LIST = BOOK_ORDER.slice(0, 39);
const NT_BOOK_LIST = BOOK_ORDER.slice(39);
const OT_BOOKS = new Set(OT_BOOK_LIST);
const NT_BOOKS = new Set(NT_BOOK_LIST);

export function BibleMapView({ followedIds, homeChurchId, churchById, onOpenVerse, onOpenSermon, sermonStackedOverThis, user, isModerator, sermonNoteChurchIds = [], setSermonNoteChurchIds = () => {}, initialTab = "map", onClose }) {
  const [activeTab, setActiveTab] = useState(initialTab);   // "map" | "insights" (leaders only, always own church)
  const [coverage, setCoverage] = useState(null);   // book -> {sermon, verse}
  const [stats, setStats] = useState(null);
  const [topVerse, setTopVerse] = useState(null);
  const [openBook, setOpenBook] = useState(null);
  const [churchesOpen, setChurchesOpen] = useState(false);

  // Same church-circles pool + persisted selection as the Sermons tab —
  // picking churches in one place is reflected in the other.
  const circlesPool = useMemo(() => {
    const list = [];
    if (homeChurchId && churchById.get(homeChurchId)) list.push(churchById.get(homeChurchId));
    for (const id of followedIds) {
      if (id === homeChurchId) continue;
      const c = churchById.get(id);
      if (c) list.push(c);
    }
    return list;
  }, [followedIds, homeChurchId, churchById]);
  const poolIds = useMemo(() => circlesPool.map(c => c.id), [circlesPool]);
  const { selectedIds, toggle: toggleCircle, selectAll: selectAllCircles, clear: clearCircles } = useChurchCircleSelection(user?.id, poolIds);

  // Insights is always the leader's own church only, regardless of the Map
  // tab's circle selection (docs/FIX-BIBLE-MAP.md item 7).
  const effectiveIds = activeTab === "insights" ? (homeChurchId ? [homeChurchId] : []) : selectedIds;
  const effectiveKey = activeTab + ":" + effectiveIds.join(",");

  useEffect(() => {
    let cancelled = false;
    setCoverage(null); setStats(null); setTopVerse(null); setOpenBook(null);
    if (effectiveIds.length === 0) { setCoverage({}); setStats(null); return; }
    (async () => {
      const [cov, st, trend] = await Promise.all([
        supabase.rpc("explore_book_coverage", { p_church_ids: effectiveIds }),
        supabase.rpc("explore_stats", { p_church_ids: effectiveIds }),
        supabase.rpc("explore_trending", { p_church_ids: effectiveIds, p_days: null, p_limit: 1 }),
      ]);
      if (cancelled) return;
      const map = {};
      for (const row of (cov.data || [])) map[row.book] = { sermon: Number(row.sermon_count)||0, verse: Number(row.verse_count)||0 };
      setCoverage(map);
      setStats(st.data && st.data[0] ? st.data[0] : null);
      setTopVerse(trend.data && trend.data[0] ? trend.data[0] : null);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveKey]);

  const neverPreached = useMemo(() => {
    if (!coverage) return [];
    return BOOK_ORDER.filter(b => !coverage[b] || !coverage[b].sermon);
  }, [coverage]);

  // Old vs New Testament split by sermon count (Insights only, client-side).
  const testamentSplit = useMemo(() => {
    if (!coverage) return null;
    let ot = 0, nt = 0;
    for (const [book, c] of Object.entries(coverage)) {
      if (OT_BOOKS.has(book)) ot += c.sermon || 0;
      else if (NT_BOOKS.has(book)) nt += c.sermon || 0;
    }
    const total = ot + nt;
    if (!total) return { otPct: 0, ntPct: 0 };
    return { otPct: Math.round((ot / total) * 100), ntPct: Math.round((nt / total) * 100) };
  }, [coverage]);

  const percentOfBible = stats?.verses_covered ? Math.round((stats.verses_covered / TOTAL_VERSES) * 100) : 0;

  return (
    <div style={{ padding: "0 22px 32px", maxWidth: 560, margin: "0 auto", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button type="button" onClick={onClose} aria-label="Close"
          style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 22, border: "none", background: "var(--white)", color: "var(--ink)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--ink)", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Bible map</h1>
      </div>

      {isModerator && (
        <div style={{display:"inline-flex", background:"#EAE3D8", borderRadius:999, padding:3, marginBottom:16}}>
          {[["map","Map"],["insights","Insights"]].map(([id,label]) => {
            const on = activeTab === id;
            return (
              <button key={id} type="button" onClick={()=>setActiveTab(id)}
                style={{
                  display:"inline-flex", alignItems:"center", gap:6,
                  background:on?"var(--white)":"transparent", color:"var(--ink)",
                  border:"none", borderRadius:999, padding:"7px 14px", fontSize:13,
                  fontWeight:on?800:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif",
                }}>
                {id === "insights" && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z"/></svg>
                )}
                {label}
              </button>
            );
          })}
        </div>
      )}

      {activeTab === "map" ? (
        <ChurchCircles pool={circlesPool} homeChurchId={homeChurchId} selectedIds={selectedIds}
          onToggle={toggleCircle} onSelectAll={selectAllCircles} onClear={() => clearCircles(homeChurchId)}
          onFindChurches={() => setChurchesOpen(true)}/>
      ) : (
        <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#F5E4B8", color:"var(--gold-ink)", borderRadius:999, padding:"6px 12px", fontSize:12, fontWeight:800, marginBottom:14}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z"/></svg>
          Leaders only · {churchById.get(homeChurchId)?.name || "Your church"}'s preaching
        </div>
      )}

      {coverage === null ? <Loading/> : (
        <>
          {activeTab === "map" ? (
            <div style={{display:"flex", gap:10, overflowX:"auto", paddingBottom:6, marginBottom:18}}>
              <StatCard label="Sermons" value={`${(stats?.sermons_total ?? 0).toLocaleString()}`}/>
              <StatCard label="Most preached book" value={stats?.top_book || "—"}/>
              <StatCard label="Most preached verse" value={topVerse ? `${topVerse.book} ${topVerse.chapter}:${topVerse.verse}` : "—"}/>
            </div>
          ) : (
            <>
              <div style={{display:"flex", gap:10, overflowX:"auto", paddingBottom:6, marginBottom:18}}>
                <StatCard label="Of the Bible" value={`${percentOfBible}%`} sub={`${(stats?.verses_covered ?? 0).toLocaleString()} of ${TOTAL_VERSES.toLocaleString()} verses`}/>
                <StatCard label="Sermons" value={`${(stats?.sermons_total ?? 0).toLocaleString()}`} sub={stats?.earliest_sermon_year ? `since ${stats.earliest_sermon_year}` : undefined}/>
                <StatCard label="Books not yet preached" value={String(neverPreached.length)}/>
              </div>

              {testamentSplit && (
                <Section title="Old vs New Testament">
                  <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6, fontSize:12.5, fontWeight:700, color:"var(--stone)"}}>
                    <span>Sermons</span>
                  </div>
                  <div style={{display:"flex", height:6, borderRadius:999, overflow:"hidden", marginBottom:6}}>
                    <div style={{flex:testamentSplit.otPct||0, background:"#CFC4F2"}}/>
                    <div style={{flex:testamentSplit.ntPct||0, background:"#7C6BC4"}}/>
                  </div>
                  <div style={{display:"flex", justifyContent:"space-between", fontSize:12.5, fontWeight:700, color:"var(--ink)"}}>
                    <span>Old {testamentSplit.otPct}%</span>
                    <span>New {testamentSplit.ntPct}%</span>
                  </div>
                </Section>
              )}

              <Section title="Not yet preached" subtitle="Ideas for a future series">
                {neverPreached.length === 0 ? (
                  <Empty>Every book has at least one sermon. 🎉</Empty>
                ) : (
                  <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                    {neverPreached.map(b => (
                      <span key={b} style={{background:"var(--white)", borderRadius:999, padding:"6px 11px", fontSize:12.5, fontWeight:600, color:"var(--ink)"}}>{b}</span>
                    ))}
                  </div>
                )}
              </Section>
            </>
          )}

          <HeatLegend/>

          <div style={{fontSize:12, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--gold-ink)", marginBottom:8}}>Old Testament</div>
          <HeatGrid coverage={coverage} onBook={setOpenBook} openBook={openBook} books={OT_BOOK_LIST}/>

          <div style={{fontSize:12, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--gold-ink)", marginTop:16, marginBottom:8}}>New Testament</div>
          <HeatGrid coverage={coverage} onBook={setOpenBook} openBook={openBook} books={NT_BOOK_LIST}/>

          {openBook && (
            <BookDetailSheet
              book={openBook}
              churchIds={effectiveIds}
              sermonCount={coverage?.[openBook]?.sermon || 0}
              churchById={churchById}
              onOpenVerse={(b, c, v) => onOpenVerse?.(b, c, v, "all")}
              onOpenSermon={onOpenSermon}
              behind={!!sermonStackedOverThis}
              onClose={()=>setOpenBook(null)}/>
          )}
        </>
      )}

      {churchesOpen && (
        <div style={{position:"fixed", inset:0, zIndex:500, background:"var(--cream)", display:"flex", flexDirection:"column"}}>
          <div style={{flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 16px 10px", borderBottom:"1px solid var(--border)"}}>
            <div style={{fontSize:17, color:"var(--ink)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:700}}>Find churches</div>
            <button onClick={()=>setChurchesOpen(false)} style={{background:"rgba(31,27,22,0.06)", border:"none", color:"var(--stone)", width:34, height:34, borderRadius:999, cursor:"pointer", fontSize:15}}>✕</button>
          </div>
          <div style={{flex:1, overflowY:"auto"}}>
            <ChurchesView churchById={churchById} activeChurchId={homeChurchId}
              sermonNoteChurchIds={sermonNoteChurchIds} setSermonNoteChurchIds={setSermonNoteChurchIds}
              user={user} onOpenChurch={()=>{}}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 3d. DISCOVER (churches / events / networks / ministries) ─────────────────
// Part 11: colour-coded left border + type badge per entity type.
const ENTITY_COLORS = { church:"#4A90E2", event:"#F59E0B", event_series:"#F59E0B", network:"#9B6FD4", ministry:"#22C55E" };
const entityColor = (t) => ENTITY_COLORS[t] || "#4A90E2";
const entityBadgeLabel = (t) =>
  t === "event" ? "Event" : t === "event_series" ? "Event series" :
  t === "network" ? "Network" : t === "ministry" ? "Ministry" : "Church";

// Entity-type predicates (module scope so useMemo deps stay clean).
const kindOf     = (e) => e.entity_type || "church";
const isChurchE  = (e) => kindOf(e) === "church";
const isNetworkE = (e) => kindOf(e) === "network";
const isMinistryE= (e) => kindOf(e) === "ministry";
const isEventE   = (e) => kindOf(e) === "event" || kindOf(e) === "event_series";
const isTopLevel = (e) => !e.parent_id;

function fmtDate(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric" }); }
  catch { return d; }
}
function eventDates(e) {
  if (e.event_start_date && e.event_end_date) return `${fmtDate(e.event_start_date)} – ${fmtDate(e.event_end_date)}`;
  if (e.event_start_date) return fmtDate(e.event_start_date);
  return null;
}

// Generic card with a type-coloured left border + optional badge / footer / body.
// onOpen makes the whole card tappable (→ profile page); the follow button and
// any body still swallow their own clicks. showArrow adds a chevron affordance.
// Set onToggle to null to hide the follow button.
function EntityCard({ e, following, locked, onToggle, onOpen, showBadge, showArrow, badge, footer, children }) {
  return (
    <div onClick={onOpen}
      style={{display:"flex", flexDirection:"column", gap:children?10:0, background:"var(--white)", border:"1px solid var(--border)", borderLeft:`3px solid ${entityColor(e.entity_type)}`, borderRadius:12, padding:"12px 14px", cursor:onOpen?"pointer":"default"}}>
      <div style={{display:"flex", alignItems:"center", gap:12}}>
        <ChurchLogo url={e.logo_url} name={e.name} size={40}/>
        <div style={{flex:1, minWidth:0}}>
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <span style={{fontSize:14.5, fontWeight:700, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{e.name}</span>
            {badge}
            {showBadge && (
              <span style={{flexShrink:0, fontSize:9.5, fontWeight:800, letterSpacing:"0.04em", textTransform:"uppercase", color:entityColor(e.entity_type), background:"rgba(31,27,22,0.06)", borderRadius:6, padding:"2px 6px"}}>
                {entityBadgeLabel(e.entity_type)}
              </span>
            )}
          </div>
          {e.location && <div style={{fontSize:12, color:"var(--stone)", marginTop:1}}>{e.location}</div>}
          {footer && <div style={{fontSize:11.5, color:"var(--stone)", marginTop:2}}>{footer}</div>}
        </div>
        {onToggle != null && (
          <span onClick={ev=>ev.stopPropagation()} style={{display:"inline-flex"}}>
            <FollowBtn following={following} locked={locked} onClick={onToggle}/>
          </span>
        )}
        {showArrow && <ChevronRight size={18} color="var(--stone)" style={{flexShrink:0}}/>}
      </div>
      {children}
    </div>
  );
}

// Compact vertical card used in the Browse / Network grids (tap → profile).
function GridCard({ e, following, locked, onToggle, onOpen }) {
  const s = Number(e.sermon_count) || 0;
  return (
    <div onClick={onOpen}
      style={{background:"var(--white)", border:"1px solid var(--border)", borderLeft:`3px solid ${entityColor(e.entity_type)}`, borderRadius:12, padding:14, display:"flex", flexDirection:"column", gap:10, cursor:"pointer"}}>
      <div style={{display:"flex", alignItems:"center", gap:10}}>
        <ChurchLogo url={e.logo_url} name={e.name} size={36}/>
        <div style={{minWidth:0, flex:1}}>
          <div style={{fontSize:14, fontWeight:700, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{e.name}</div>
          {e.location && <div style={{fontSize:11.5, color:"var(--stone)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{e.location}</div>}
        </div>
      </div>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:8}}>
        <span style={{fontSize:11.5, color:"var(--stone)"}}>{s.toLocaleString()} sermon{s===1?"":"s"}</span>
        <span onClick={ev=>ev.stopPropagation()} style={{display:"inline-flex"}}>
          <FollowBtn small following={following} locked={locked} onClick={onToggle}/>
        </span>
      </div>
    </div>
  );
}

// Full "Following" list page (Part 5) — grouped by entity type, unfollow + open.
function FollowingListOverlay({ home, followed, activeChurchId, onToggle, onOpen, onClose }) {
  const churches   = followed.filter(isChurchE);
  const events     = followed.filter(isEventE);
  const networks   = followed.filter(isNetworkE);
  const ministries = followed.filter(isMinistryE);
  const row = (e, typeLabel) => {
    const s = Number(e.sermon_count) || 0;
    return (
      <div key={e.id} onClick={()=>onOpen(e)}
        style={{display:"flex", alignItems:"center", gap:12, background:"var(--white)", border:"1px solid var(--border)", borderRadius:12, padding:"12px 14px", cursor:"pointer"}}>
        <ChurchLogo url={e.logo_url} name={e.name} size={38}/>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:14.5, fontWeight:700, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{e.name}</div>
          <div style={{fontSize:11.5, color:"var(--stone)", marginTop:1}}>{e.location || typeLabel}{s ? ` · ${s.toLocaleString()} sermon${s===1?"":"s"}` : ""}</div>
        </div>
        {e.id !== activeChurchId ? (
          <button type="button" onClick={ev=>{ ev.stopPropagation(); onToggle(e.id); }}
            style={{flexShrink:0, background:"transparent", border:"1px solid rgba(249,112,102,0.5)", color:"#C8372A", borderRadius:9, padding:"6px 12px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Unfollow</button>
        ) : (
          <span style={{flexShrink:0, fontSize:11, fontWeight:700, color:"var(--stone)"}}>Home</span>
        )}
        <ChevronRight size={18} color="var(--stone)" style={{flexShrink:0}}/>
      </div>
    );
  };
  const empty = !home && churches.length===0 && events.length===0 && networks.length===0 && ministries.length===0;
  return (
    <div style={{position:"fixed", inset:0, zIndex:360, background:"var(--cream)", display:"flex", flexDirection:"column", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
      <div style={{flexShrink:0, borderBottom:"1px solid var(--border)"}}>
        <div style={{maxWidth:560, margin:"0 auto", width:"100%", boxSizing:"border-box", padding:"14px 16px", display:"flex", alignItems:"center", gap:12}}>
          <button type="button" onClick={onClose} aria-label="Back" style={{background:"none", border:"none", color:"var(--stone)", cursor:"pointer", fontSize:22, padding:0, display:"flex", lineHeight:1}}>←</button>
          <div style={{fontSize:18, fontWeight:800, color:"var(--ink)"}}>Following</div>
        </div>
      </div>
      <div style={{flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch"}}>
        <div style={{maxWidth:560, margin:"0 auto", padding:"16px 16px max(28px, env(safe-area-inset-bottom))", boxSizing:"border-box"}}>
          {home && <Section title="My church"><div>{row(home, "Home church")}</div></Section>}
          {churches.length > 0 && <Section title={`Churches (${churches.length})`}><div style={{display:"flex", flexDirection:"column", gap:10}}>{churches.map(e=>row(e,"Church"))}</div></Section>}
          {events.length > 0 && <Section title={`Events (${events.length})`}><div style={{display:"flex", flexDirection:"column", gap:10}}>{events.map(e=>row(e,"Event"))}</div></Section>}
          {networks.length > 0 && <Section title={`Networks (${networks.length})`}><div style={{display:"flex", flexDirection:"column", gap:10}}>{networks.map(e=>row(e,"Network"))}</div></Section>}
          {ministries.length > 0 && <Section title={`Ministries (${ministries.length})`}><div style={{display:"flex", flexDirection:"column", gap:10}}>{ministries.map(e=>row(e,"Ministry"))}</div></Section>}
          {empty && <Empty>You're not following anything yet.</Empty>}
          <button type="button" onClick={onClose}
            style={{display:"block", width:"100%", marginTop:10, background:"transparent", border:`1px solid ${GOLD}`, color:"var(--gold-ink)", borderRadius:10, padding:"11px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
            Find more churches &amp; events →
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChurchesView({ churchById, activeChurchId, sermonNoteChurchIds, setSermonNoteChurchIds, user, onOpenChurch }) {
  const [entities, setEntities] = useState(null);   // merged churches + entities
  const [search, setSearch] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const [expandedSeries, setExpandedSeries] = useState(() => new Set());
  const [followingOpen, setFollowingOpen] = useState(false);    // Part 5 — full "Following" list
  const [browseFilter, setBrowseFilter] = useState("popular");  // popular | near | network
  const [browseLimit, setBrowseLimit] = useState(6);
  const [pastOpen, setPastOpen] = useState(false);
  const searchRef = useRef(null);
  const open = (e) => onOpenChurch?.(e);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Per-source sermon/verse counts (best-effort).
      const statsRes = await supabase.rpc("church_stats");
      const statsById = new Map();
      if (Array.isArray(statsRes.data)) for (const s of statsRes.data) statsById.set(s.church_id, s);

      // Entity rows — try the full entity columns, fall back if the migration
      // (entity_type / parent_id / event dates) hasn't run yet.
      let sel = await supabase.from("churches")
        .select("id,name,location,logo_url,network_id,parent_id,entity_type,event_start_date,event_end_date,created_at").order("name");
      if (sel.error) {
        sel = await supabase.from("churches").select("id,name,location,logo_url,network_id,created_at").order("name");
      }
      if (cancelled) return;
      const rows = sel.data || [];
      if (rows.length === 0 && Array.isArray(statsRes.data)) {
        // Last resort: build entities straight from church_stats.
        setEntities(statsRes.data.map(s => ({
          id:s.church_id, name:s.name, location:s.location, logo_url:s.logo_url,
          network_id:s.network_id, network_name:s.network_name, parent_id:null, entity_type:null,
          event_start_date:null, event_end_date:null, created_at:s.created_at,
          sermon_count:Number(s.sermon_count)||0, verse_count:Number(s.verse_count)||0,
        })));
        return;
      }
      setEntities(rows.map(r => {
        const s = statsById.get(r.id) || {};
        return {
          id:r.id, name:r.name, location:r.location || s.location || null, logo_url:r.logo_url || s.logo_url || null,
          network_id:r.network_id || s.network_id || null, network_name:s.network_name || null,
          parent_id:r.parent_id || null, entity_type:r.entity_type || null,
          event_start_date:r.event_start_date || null, event_end_date:r.event_end_date || null,
          created_at:r.created_at || s.created_at || null,
          sermon_count:Number(s.sermon_count)||0, verse_count:Number(s.verse_count)||0,
        };
      }));
    })();
    return () => { cancelled = true; };
  }, []);

  const extras = useMemo(() => (sermonNoteChurchIds || []).filter(id => id && id !== activeChurchId), [sermonNoteChurchIds, activeChurchId]);
  const followingSet = useMemo(() => new Set([...extras, activeChurchId].filter(Boolean)), [extras, activeChurchId]);

  function toggleFollow(id) {
    if (!id || id === activeChurchId) return;
    if (followingSet.has(id)) setSermonNoteChurchIds([activeChurchId, ...extras.filter(x => x !== id)].filter(Boolean));
    else setSermonNoteChurchIds([activeChurchId, ...extras, id].filter(Boolean));
  }
  function followMany(ids) {
    const add = ids.filter(id => id && id !== activeChurchId && !followingSet.has(id));
    if (add.length) setSermonNoteChurchIds([activeChurchId, ...extras, ...add].filter(Boolean));
  }
  // Follow all: local state (parent auto-syncs to user_church_follows) + a
  // direct upsert of the child rows per spec (belt-and-suspenders; harmless
  // duplicates thanks to onConflict).
  async function followAllNetwork(g) {
    const ids = [g.entity.id, ...g.members.map(m => m.id)].filter(Boolean);
    followMany(ids);
    if (user?.id) {
      const rows = ids.filter(id => id !== activeChurchId).map(church_id => ({ user_id:user.id, church_id }));
      if (rows.length) {
        try { await supabase.from("user_church_follows").upsert(rows, { onConflict:"user_id,church_id" }); }
        catch (e) { console.warn("follow all failed:", e?.message || e); }
      }
    }
  }
  function toggleSeries(id) {
    setExpandedSeries(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  }

  // parent_id -> [child entities]
  const childrenOf = useMemo(() => {
    const m = new Map();
    for (const e of entities || []) { if (e.parent_id) { if (!m.has(e.parent_id)) m.set(e.parent_id, []); m.get(e.parent_id).push(e); } }
    return m;
  }, [entities]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const q = search.trim().toLowerCase();

  // Search across ALL entity types, grouped by type.
  const searchGroups = useMemo(() => {
    if (!entities || !q) return null;
    const match = e => (e.name||"").toLowerCase().includes(q) || (e.location||"").toLowerCase().includes(q) || (e.network_name||"").toLowerCase().includes(q);
    const res = entities.filter(match);
    return { churches:res.filter(isChurchE), events:res.filter(isEventE), networks:res.filter(isNetworkE), ministries:res.filter(isMinistryE) };
  }, [entities, q]);

  // Your area — churches sharing the home church's (or profile's) location.
  const areaLoc = useMemo(() =>
    (churchById.get(activeChurchId)?.location || user?.location || "").toLowerCase().trim(),
    [churchById, activeChurchId, user]);
  // Events (top-level only; child years surface on expand).
  const eventsTop = useMemo(() => entities ? entities.filter(e => isEventE(e) && isTopLevel(e)) : [], [entities]);
  const upcoming  = useMemo(() => eventsTop.filter(e => e.event_start_date && e.event_start_date > today), [eventsTop, today]);
  const ongoing   = useMemo(() => eventsTop.filter(e => kindOf(e) === "event_series" && !e.event_start_date && !e.event_end_date), [eventsTop]);
  const past      = useMemo(() => eventsTop.filter(e => e.event_end_date && e.event_end_date < today), [eventsTop, today]);

  // ── Membership-aware sections (Part 2 scenarios) ──
  // Home church (from the entity list, falling back to the churchById lookup).
  const home = useMemo(() =>
    (entities || []).find(e => e.id === activeChurchId) || (activeChurchId ? churchById.get(activeChurchId) : null),
    [entities, activeChurchId, churchById]);

  // The network the home church belongs to (parent_id or legacy network_id).
  const homeNetwork = useMemo(() => {
    if (!home || !entities) return null;
    const pid = home.parent_id || home.network_id;
    if (!pid) return null;
    const net = entities.find(e => e.id === pid && isNetworkE(e));
    if (net) return net;
    if (home.network_id && home.network_name) return { id:home.network_id, name:home.network_name, entity_type:"network" };
    return null;
  }, [home, entities]);

  const networkMembers = useMemo(() => {
    if (!homeNetwork || !entities) return [];
    return entities.filter(e => e.id !== activeChurchId && (e.parent_id === homeNetwork.id || e.network_id === homeNetwork.id));
  }, [homeNetwork, entities, activeChurchId]);
  const networkMemberIds = useMemo(() => new Set(networkMembers.map(m => m.id)), [networkMembers]);

  // Everything the user follows (excluding the home church) — for the "Following"
  // list and the "Churches I follow" section.
  const followedEntities = useMemo(() =>
    (entities || []).filter(e => e.id !== activeChurchId && followingSet.has(e.id)),
    [entities, followingSet, activeChurchId]);
  // "Churches I follow" excludes network churches already shown above.
  const iFollow = useMemo(() =>
    followedEntities.filter(e => !networkMemberIds.has(e.id)),
    [followedEntities, networkMemberIds]);

  // Browse churches — exclude home + anything already followed.
  const browseBase = useMemo(() =>
    (entities || []).filter(e => isChurchE(e) && e.id !== activeChurchId && !followingSet.has(e.id)),
    [entities, activeChurchId, followingSet]);
  const browseNear = useMemo(() => {
    if (!areaLoc) return [];
    return browseBase.filter(e => (e.location||"").toLowerCase().includes(areaLoc) || areaLoc.includes((e.location||"").toLowerCase().trim()));
  }, [browseBase, areaLoc]);
  const browseChurches = useMemo(() => {
    if (browseFilter === "near") return browseNear;
    // popular + network both order by sermon coverage; network adds dividers at render.
    return [...browseBase].sort((a,b)=>(Number(b.sermon_count)||0)-(Number(a.sermon_count)||0));
  }, [browseFilter, browseBase, browseNear]);
  // For "By network": group the sorted browse list under their network name.
  const browseByNetwork = useMemo(() => {
    const netName = (e) => e.network_name || (e.parent_id && (entities||[]).find(x=>x.id===e.parent_id)?.name) || "Independent";
    const groups = new Map();
    for (const e of browseChurches) {
      const k = netName(e);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(e);
    }
    return [...groups.entries()];
  }, [browseChurches, entities]);

  useEffect(() => { setBrowseLimit(6); }, [browseFilter]);

  const scenario = !activeChurchId || !home ? "C" : (homeNetwork ? "A" : "B");

  const churchFooter = (e) =>
    `${(Number(e.sermon_count)||0).toLocaleString()} sermon${Number(e.sermon_count)===1?"":"s"} · ${(Number(e.verse_count)||0).toLocaleString()} verse note${Number(e.verse_count)===1?"":"s"}`;

  const renderChurch = (e, showBadge) => (
    <EntityCard key={e.id} e={e} showBadge={showBadge} onOpen={()=>open(e)}
      following={followingSet.has(e.id)} locked={e.id===activeChurchId}
      onToggle={()=>toggleFollow(e.id)} footer={churchFooter(e)}/>
  );

  const childRow = (k) => (
    <div key={k.id} onClick={()=>open(k)} style={{display:"flex", alignItems:"center", gap:10, cursor:"pointer"}}>
      <ChurchLogo url={k.logo_url} name={k.name} size={24}/>
      <span style={{flex:1, minWidth:0, fontSize:13, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{k.name}</span>
      <span onClick={ev=>ev.stopPropagation()} style={{display:"inline-flex"}}>
        <FollowBtn small following={followingSet.has(k.id)} locked={k.id===activeChurchId} onClick={()=>toggleFollow(k.id)}/>
      </span>
    </div>
  );

  const renderEvent = (e) => {
    const isSeries = kindOf(e) === "event_series";
    const kids = childrenOf.get(e.id) || [];
    const sermons = (Number(e.sermon_count)||0) + kids.reduce((s,c)=>s+(Number(c.sermon_count)||0), 0);
    const footer = isSeries
      ? `${kids.length} year${kids.length===1?"":"s"} · ${sermons.toLocaleString()} sermon${sermons===1?"":"s"}`
      : (eventDates(e) || `${(Number(e.sermon_count)||0).toLocaleString()} sermon${Number(e.sermon_count)===1?"":"s"}`);
    const expanded = expandedSeries.has(e.id);
    return (
      <EntityCard key={e.id} e={e} showBadge onOpen={()=>open(e)}
        following={followingSet.has(e.id)} locked={e.id===activeChurchId}
        onToggle={()=>toggleFollow(e.id)} footer={footer}>
        {isSeries && kids.length > 0 && (
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            <button type="button" onClick={()=>toggleSeries(e.id)}
              style={{alignSelf:"flex-start", background:"none", border:"none", color:"var(--gold-ink)", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", padding:0}}>
              {expanded ? "Hide years" : `Show ${kids.length} year${kids.length===1?"":"s"}`}
            </button>
            {expanded && <div style={{display:"flex", flexDirection:"column", gap:6}}>{kids.map(childRow)}</div>}
          </div>
        )}
      </EntityCard>
    );
  };

  const renderNetwork = (g) => {
    const allFollowed = followingSet.has(g.entity.id) && g.members.every(c => followingSet.has(c.id));
    return (
      <div key={g.entity.id} style={{background:"var(--white)", border:"1px solid var(--border-strong)", borderLeft:`3px solid ${entityColor("network")}`, borderRadius:14, padding:"14px 16px"}}>
        <div onClick={()=>open(g.entity)} style={{display:"flex", alignItems:"center", gap:10, marginBottom:g.members.length?10:0, cursor:"pointer"}}>
          <div style={{display:"flex"}}>
            {g.members.slice(0,3).map((c,j)=>(
              <span key={c.id} style={{marginLeft:j===0?0:-8, border:"1.5px solid #0d0d0d", borderRadius:9}}>
                <ChurchLogo url={c.logo_url} name={c.name} size={30}/>
              </span>
            ))}
          </div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:15, fontWeight:800, color:"var(--ink)"}}>{g.entity.name}</div>
            <div style={{fontSize:11.5, color:"var(--stone)"}}>{g.members.length} church{g.members.length===1?"":"es"} · {g.sermons.toLocaleString()} sermon{g.sermons===1?"":"s"}</div>
          </div>
          <span onClick={ev=>ev.stopPropagation()} style={{display:"inline-flex"}}>
            <button type="button" onClick={()=>followAllNetwork(g)} disabled={allFollowed}
              style={{flexShrink:0, background:allFollowed?"transparent":GOLD, color:allFollowed?"var(--stone)":"var(--ink)", border:allFollowed?"1px solid var(--border-strong)":"none", borderRadius:10, padding:"8px 12px", fontSize:12.5, fontWeight:800, cursor:allFollowed?"default":"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
              {allFollowed ? "Following all" : "Follow all"}
            </button>
          </span>
        </div>
        {g.members.length > 0 && (
          <div style={{display:"flex", flexDirection:"column", gap:6}}>{g.members.slice(0,4).map(childRow)}</div>
        )}
        {g.members.length > 4 && (
          <button type="button" onClick={()=>open(g.entity)}
            style={{marginTop:10, background:"none", border:"none", color:"var(--gold-ink)", fontSize:12.5, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", padding:0}}>
            See all {g.members.length} churches in {g.entity.name} →
          </button>
        )}
      </div>
    );
  };

  const MY_CHURCH_BADGE = (
    <span style={{flexShrink:0, fontSize:9.5, fontWeight:800, letterSpacing:"0.04em", textTransform:"uppercase", color:"var(--ink)", background:GOLD, borderRadius:6, padding:"2px 7px"}}>My Church</span>
  );

  return (
    <div>
      <h2 style={{fontSize:22, fontWeight:800, color:"var(--ink)", margin:"0 0 10px"}}>Discover</h2>

      {/* SEARCH BAR (always first) */}
      <div style={{position:"relative", marginBottom:18}}>
        <Search size={15} color="var(--stone)" style={{position:"absolute", left:12, top:"50%", transform:"translateY(-50%)"}}/>
        <input ref={searchRef} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search churches, events & networks…"
          style={{width:"100%", boxSizing:"border-box", background:"var(--white)", border:"1px solid var(--border-strong)", borderRadius:10, padding:"10px 34px 10px 34px", color:"var(--ink)", fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none"}}/>
        {search && (
          <button type="button" onClick={()=>setSearch("")} aria-label="Clear search"
            style={{position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"var(--stone)", fontSize:18, cursor:"pointer", padding:4, lineHeight:1}}>✕</button>
        )}
      </div>

      {entities === null ? <Loading/> : q ? (
        // ── SEARCH RESULTS (Part 7) — grouped by type ──
        (() => {
          const g = searchGroups;
          const total = g ? g.churches.length + g.events.length + g.networks.length + g.ministries.length : 0;
          if (!total) return (
            <Empty>
              <div style={{marginBottom:12}}>No results for "{search}".</div>
              <button type="button" onClick={()=>setReqOpen(true)}
                style={{background:"transparent", border:`1px solid ${GOLD}`, color:"var(--gold-ink)", borderRadius:9, padding:"8px 14px", fontSize:12.5, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                Request "{search.trim()}" to be added →
              </button>
            </Empty>
          );
          return (
            <>
              {g.churches.length > 0 && (
                <Section title={`Churches (${g.churches.length})`}><div style={{display:"flex", flexDirection:"column", gap:10}}>{g.churches.map(e=>renderChurch(e, true))}</div></Section>
              )}
              {g.events.length > 0 && (
                <Section title={`Events (${g.events.length})`}><div style={{display:"flex", flexDirection:"column", gap:10}}>{g.events.map(renderEvent)}</div></Section>
              )}
              {g.networks.length > 0 && (
                <Section title={`Networks (${g.networks.length})`}><div style={{display:"flex", flexDirection:"column", gap:12}}>{g.networks.map(n=>renderNetwork({ entity:n, members:(entities||[]).filter(e=>e.parent_id===n.id||e.network_id===n.id), sermons:0 }))}</div></Section>
              )}
              {g.ministries.length > 0 && (
                <Section title={`Ministries (${g.ministries.length})`}><div style={{display:"flex", flexDirection:"column", gap:10}}>{g.ministries.map(e=>renderChurch(e, true))}</div></Section>
              )}
            </>
          );
        })()
      ) : (
        <>
          {/* MY CHURCH (or Find-your-church CTA in Scenario C) */}
          {scenario === "C" ? (
            <Section title="My church">
              <div style={{background:"var(--white)", border:"1px solid var(--border)", borderRadius:12, padding:"18px 16px", textAlign:"center"}}>
                <div style={{fontSize:16, fontWeight:800, color:"var(--ink)", marginBottom:4}}>Find your church</div>
                <div style={{fontSize:13, color:"var(--stone)", lineHeight:1.5, marginBottom:14}}>Search to see if your church is already on the platform.</div>
                <button type="button" onClick={()=>{ searchRef.current?.focus(); searchRef.current?.scrollIntoView({block:"center", behavior:"smooth"}); }}
                  style={{background:GOLD, color:"var(--ink)", border:"none", borderRadius:10, padding:"10px 18px", fontSize:13.5, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                  Search for my church →
                </button>
              </div>
            </Section>
          ) : home && (
            <Section title="My church">
              <EntityCard e={home} onOpen={()=>open(home)} showArrow onToggle={null} badge={MY_CHURCH_BADGE} footer={churchFooter(home)}/>
            </Section>
          )}

          {/* NETWORK (Scenario A only) — distinct #0d0d0d panel */}
          {scenario === "A" && homeNetwork && (
            <div style={{background:"var(--white)", border:"1px solid var(--border)", borderRadius:14, padding:"14px 14px 16px", marginBottom:22}}>
              <div style={{fontSize:11, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:"var(--gold-ink)", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>{homeNetwork.name}</div>
              <div style={{fontSize:12, color:"var(--stone)", margin:"3px 0 12px"}}>Other churches in your network</div>
              {networkMembers.length === 0 ? <Empty>No other churches in this network yet.</Empty> : (
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10}}>
                  {networkMembers.slice(0,4).map(e => (
                    <GridCard key={e.id} e={e} following={followingSet.has(e.id)} locked={e.id===activeChurchId} onToggle={()=>toggleFollow(e.id)} onOpen={()=>open(e)}/>
                  ))}
                </div>
              )}
              {networkMembers.length > 4 && (
                <button type="button" onClick={()=>open(homeNetwork)}
                  style={{marginTop:12, background:"none", border:"none", color:"var(--gold-ink)", fontSize:12.5, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", padding:0}}>
                  See all {networkMembers.length} churches in {homeNetwork.name} →
                </button>
              )}
            </div>
          )}

          {/* CHURCHES I FOLLOW */}
          {iFollow.length > 0 && (
            <Section title={`Churches I follow (${iFollow.length})`}>
              <div style={{display:"flex", flexDirection:"column", gap:10}}>
                {iFollow.slice(0,3).map(e => (
                  <EntityCard key={e.id} e={e} showBadge showArrow onToggle={null} onOpen={()=>open(e)} footer={churchFooter(e)}/>
                ))}
              </div>
              {iFollow.length > 3 && (
                <button type="button" onClick={()=>setFollowingOpen(true)}
                  style={{marginTop:10, background:"none", border:"none", color:"var(--gold-ink)", fontSize:12.5, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", padding:0}}>
                  See all {followedEntities.length} →
                </button>
              )}
            </Section>
          )}

          {/* BROWSE CHURCHES */}
          <Section title="Browse churches">
            <div style={{display:"flex", gap:8, overflowX:"auto", paddingBottom:8, marginBottom:12, WebkitOverflowScrolling:"touch"}}>
              <button type="button" onClick={()=>setBrowseFilter("popular")} style={pillStyle(browseFilter==="popular")}>Popular</button>
              <button type="button" onClick={()=>setBrowseFilter("near")} style={pillStyle(browseFilter==="near")}>Near me</button>
              <button type="button" onClick={()=>setBrowseFilter("network")} style={pillStyle(browseFilter==="network")}>By network</button>
            </div>
            {browseChurches.length === 0 ? (
              <Empty>{browseFilter==="near" ? "No churches found near you yet." : "No more churches to browse."}</Empty>
            ) : browseFilter === "network" ? (
              <div style={{display:"flex", flexDirection:"column", gap:16}}>
                {browseByNetwork.map(([netName, list]) => (
                  <div key={netName}>
                    <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"var(--stone)", margin:"0 0 8px"}}>{netName}</div>
                    <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:10}}>
                      {list.slice(0,browseLimit).map(e => (
                        <GridCard key={e.id} e={e} following={followingSet.has(e.id)} locked={e.id===activeChurchId} onToggle={()=>toggleFollow(e.id)} onOpen={()=>open(e)}/>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:10}}>
                  {browseChurches.slice(0,browseLimit).map(e => (
                    <GridCard key={e.id} e={e} following={followingSet.has(e.id)} locked={e.id===activeChurchId} onToggle={()=>toggleFollow(e.id)} onOpen={()=>open(e)}/>
                  ))}
                </div>
                {browseChurches.length > browseLimit && (
                  <button type="button" onClick={()=>setBrowseLimit(l=>l+6)}
                    style={{display:"block", width:"100%", marginTop:12, background:"var(--white)", border:"1px solid var(--border-strong)", color:"var(--gold-ink)", borderRadius:10, padding:"11px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    Load more
                  </button>
                )}
              </>
            )}
          </Section>

          {/* BROWSE EVENTS */}
          {(upcoming.length > 0 || ongoing.length > 0 || past.length > 0) && (
            <Section title="Browse events">
              {upcoming.length > 0 && (
                <>
                  <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--stone)", margin:"0 0 8px"}}>Upcoming</div>
                  <div style={{display:"flex", flexDirection:"column", gap:10, marginBottom:14}}>
                    {upcoming.map(e => {
                      const s = Number(e.sermon_count)||0;
                      return (
                        <EntityCard key={e.id} e={e} showBadge onOpen={()=>open(e)}
                          following={followingSet.has(e.id)} locked={e.id===activeChurchId} onToggle={()=>toggleFollow(e.id)}
                          footer={<>📅 {eventDates(e) || "Upcoming"}{s ? ` · ${s} sermon${s===1?"":"s"}` : ""}{s===0 ? <span style={{display:"block", color:"var(--gold-ink)", marginTop:2}}>Get notified when sermons are added</span> : null}</>}/>
                      );
                    })}
                  </div>
                </>
              )}
              {ongoing.length > 0 && (
                <>
                  <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--stone)", margin:"0 0 8px"}}>Series</div>
                  <div style={{display:"flex", flexDirection:"column", gap:10, marginBottom:14}}>{ongoing.map(renderEvent)}</div>
                </>
              )}
              {past.length > 0 && (
                <>
                  <button type="button" onClick={()=>setPastOpen(o=>!o)}
                    style={{background:"none", border:"none", color:"var(--stone)", fontSize:12.5, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", padding:0, margin:"0 0 8px"}}>
                    {pastOpen ? "Hide past events" : `Show past events (${past.length})`}
                  </button>
                  {pastOpen && (
                    <div style={{display:"flex", flexDirection:"column", gap:10, opacity:0.7}}>{past.map(renderEvent)}</div>
                  )}
                </>
              )}
            </Section>
          )}
        </>
      )}

      {/* FOOTER CTA */}
      <div style={{marginTop:22, paddingTop:16, borderTop:"1px solid var(--border)", textAlign:"center"}}>
        <div style={{fontSize:13, color:"var(--stone)", marginBottom:12}}>Don't see your church or event?</div>
        <div style={{display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap"}}>
          <button type="button" onClick={()=>setReqOpen(true)}
            style={{background:"transparent", border:`1px solid ${GOLD}`, color:"var(--gold-ink)", borderRadius:10, padding:"10px 16px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
            Request a church or event
          </button>
          <a href="/?onboarding=1"
            style={{background:GOLD, color:"var(--ink)", borderRadius:10, padding:"10px 16px", fontSize:13, fontWeight:800, textDecoration:"none", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
            Set up my church →
          </a>
        </div>
      </div>

      {reqOpen && <RequestChurchSheet user={user} initialName={q ? search.trim() : ""} onClose={()=>setReqOpen(false)}/>}
      {followingOpen && (
        <FollowingListOverlay
          home={home} followed={followedEntities} activeChurchId={activeChurchId}
          onToggle={toggleFollow} onOpen={(e)=>{ setFollowingOpen(false); open(e); }} onClose={()=>setFollowingOpen(false)}/>
      )}
    </div>
  );
}

function FollowBtn({ following, locked, small, onClick }) {
  if (locked) return (
    <span style={{flexShrink:0, fontSize:small?11:12.5, fontWeight:700, color:"var(--stone)"}}>Home</span>
  );
  return (
    <button type="button" onClick={onClick}
      style={{
        flexShrink:0, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:small?12:13, fontWeight:800, cursor:"pointer",
        borderRadius:9, padding:small?"5px 10px":"8px 14px",
        background: following ? GOLD : "transparent", color: following ? "var(--ink)" : "var(--gold-ink)",
        border:`1px solid ${GOLD}`,
      }}>
      {following ? "Following" : "Follow"}
    </button>
  );
}

// Request-a-church/event/network bottom sheet (same flow as onboarding/profile).
function RequestChurchSheet({ user, onClose, initialName = "" }) {
  const [kind, setKind] = useState("church");   // church | event | network
  const [name, setName] = useState(initialName);
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const kindLabel = kind === "event" ? "event" : kind === "network" ? "network" : "church";

  async function submit() {
    if (!name.trim() || !location.trim()) { setError(`${kindLabel[0].toUpperCase()+kindLabel.slice(1)} name and location are required.`); return; }
    setBusy(true); setError("");
    const requesterName  = (user?.full_name || user?.email?.split("@")[0] || "").trim();
    const requesterEmail = (user?.email || "").trim();
    try {
      const base = {
        requested_by: user?.id || null, church_name: name.trim(), location: location.trim(),
        requester_name: requesterName || null, requester_email: requesterEmail || null,
      };
      // Include entity_type; retry without it if the column doesn't exist yet.
      let { error: insErr } = await supabase.from("church_requests").insert({ ...base, entity_type: kind });
      if (insErr) {
        const retry = await supabase.from("church_requests").insert(base);
        if (retry.error) console.warn("church_requests insert failed:", retry.error.message);
      }
      await supabase.functions.invoke("submit-church-enquiry", {
        body: { type: kind === "church" ? "church_request" : `${kind}_request`, entity_type: kind, name: requesterName || "A user", email: requesterEmail, church: name.trim(), location: location.trim() },
      });
      setSent(true);
    } catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div onClick={onClose} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300}}/>
      <div style={{position:"fixed", left:"50%", bottom:0, transform:"translateX(-50%)", zIndex:301, width:"100%", maxWidth:430, background:"var(--white)", borderRadius:"24px 24px 0 0", borderTop:"1px solid var(--border-strong)", padding:"22px 22px max(22px, env(safe-area-inset-bottom))", boxSizing:"border-box", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
        <div style={{width:38,height:4,borderRadius:2,background:"rgba(31,27,22,0.18)",margin:"0 auto 16px"}}/>
        {sent ? (
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:"var(--ink)",marginBottom:6}}>Request sent!</div>
            <div style={{fontSize:14,color:"var(--stone)",lineHeight:1.5,marginBottom:18}}>We'll add {name.trim() || `the ${kindLabel}`} and let you know — usually within 24 hours.</div>
            <button type="button" onClick={onClose} style={{width:"100%",background:GOLD,color:"var(--ink)",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer"}}>Done</button>
          </div>
        ) : (
          <>
            <div style={{fontSize:20,fontWeight:800,color:"var(--ink)",marginBottom:4}}>Request we add a {kindLabel}</div>
            <div style={{fontSize:14,color:"var(--stone)",lineHeight:1.5,marginBottom:14}}>We'll add all of its recorded sermons — usually within 24 hours.</div>
            <div style={{display:"flex", gap:6, marginBottom:12}}>
              {[["church","Church"],["event","Event"],["network","Network"]].map(([id,label]) => {
                const on = kind === id;
                return (
                  <button key={id} type="button" onClick={()=>setKind(id)}
                    style={{flex:1, background:on?GOLD:"var(--white)", color:on?"var(--ink)":"var(--stone)", border:on?"none":"1px solid var(--border-strong)", borderRadius:9, padding:"8px 6px", fontSize:12.5, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    {label}
                  </button>
                );
              })}
            </div>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder={`${kindLabel[0].toUpperCase()+kindLabel.slice(1)} name`}
              style={{width:"100%",boxSizing:"border-box",background:"var(--white)",border:"1px solid var(--border-strong)",borderRadius:10,padding:"11px 13px",color:"var(--ink)",fontSize:14,marginBottom:10,outline:"none",fontFamily:"'Plus Jakarta Sans',sans-serif"}}/>
            <input value={location} onChange={e=>setLocation(e.target.value)} placeholder="Town / city"
              style={{width:"100%",boxSizing:"border-box",background:"var(--white)",border:"1px solid var(--border-strong)",borderRadius:10,padding:"11px 13px",color:"var(--ink)",fontSize:14,marginBottom:14,outline:"none",fontFamily:"'Plus Jakarta Sans',sans-serif"}}/>
            {error && <div style={{fontSize:12,color:"#C8372A",marginBottom:10}}>{error}</div>}
            <button type="button" onClick={submit} disabled={busy}
              style={{width:"100%",background:GOLD,color:"var(--ink)",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:busy?"default":"pointer",opacity:busy?0.5:1}}>
              {busy ? "Sending…" : "Send request →"}
            </button>
            <button type="button" onClick={onClose} style={{display:"block",margin:"10px auto 0",background:"none",border:"none",color:"var(--stone)",fontSize:13,cursor:"pointer",textDecoration:"underline"}}>Cancel</button>
          </>
        )}
      </div>
    </>
  );
}

// Book overlay — verses preached in a book + which churches preached them.
function BookDetailSheet({ book, churchIds, sermonCount, churchById, onOpenVerse, onOpenSermon, onClose, behind }) {
  const [rows, setRows] = useState(null);
  const [verseTextMap, setVerseTextMap] = useState({});   // "ch:v" -> verse text
  const [sermonRows, setSermonRows] = useState(null);     // compact "sermons on this book" list
  useEffect(() => {
    let cancelled = false;
    setRows(null);
    supabase.rpc("explore_book_verses", { p_church_ids: churchIds, p_book: book })
      .then(({ data, error }) => { if (cancelled) return; setRows(error || !Array.isArray(data) ? [] : data); });
    return () => { cancelled = true; };
  }, [book, churchIds]);

  useEffect(() => {
    let cancelled = false;
    setSermonRows(null);
    (async () => {
      let q = supabase.from("sermon_refs").select("sermon_id, sermons(id, church_id, title, display_title, preacher, date, youtube_id, duration)").eq("book", book);
      if (churchIds && churchIds.length) q = q.in("sermons.church_id", churchIds);
      const { data, error } = await q;
      if (cancelled) return;
      if (error || !Array.isArray(data)) { setSermonRows([]); return; }
      const byId = new Map();
      for (const r of data) { if (r.sermons) byId.set(r.sermons.id, r.sermons); }
      const list = [...byId.values()].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setSermonRows(list);
    })();
    return () => { cancelled = true; };
  }, [book, churchIds]);

  // Chapter-level coverage + most-preached chapter, from the already-fetched
  // verse rows (sum of sermon_count across each chapter's verses).
  const chapterCoverage = useMemo(() => {
    const m = {};
    for (const r of (rows || [])) m[r.chapter] = (m[r.chapter] || 0) + (Number(r.sermon_count) || 0);
    return m;
  }, [rows]);
  const mostPreachedChapter = useMemo(() => {
    let best = null, bestN = -1;
    for (const [ch, n] of Object.entries(chapterCoverage)) { if (n > bestN) { bestN = n; best = Number(ch); } }
    return best;
  }, [chapterCoverage]);
  const chapterCount = BOOK_CHAPTERS[book] || 1;
  const versesPreached = (rows || []).length;

  // Fetch the scripture text (public-domain WEB) for each chapter that has
  // preached verses, so each row can show the verse text. Bounded to the
  // chapters actually present.
  useEffect(() => {
    if (!rows || rows.length === 0) { setVerseTextMap({}); return; }
    let cancelled = false;
    const chs = [...new Set(rows.map(r => r.chapter))];
    (async () => {
      const map = {};
      await Promise.all(chs.map(async (ch) => {
        try {
          const r = await fetch(`https://bible-api.com/${encodeURIComponent(book)}+${ch}?translation=web`);
          if (!r.ok) return;
          const d = await r.json();
          for (const v of (d.verses || [])) map[`${ch}:${v.verse}`] = String(v.text || "").replace(/\s+/g, " ").trim();
        } catch (_e) { /* leave blank for this chapter */ }
      }));
      if (!cancelled) setVerseTextMap(map);
    })();
    return () => { cancelled = true; };
  }, [rows, book]);

  const chapters = useMemo(() => {
    const m = new Map();
    for (const r of (rows || [])) {
      if (!m.has(r.chapter)) m.set(r.chapter, []);
      m.get(r.chapter).push(r);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [rows]);

  return (
    <>
      <div onClick={onClose} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300}}/>
      <div style={{position:"fixed", left:"50%", bottom:0, zIndex:301, width:"100%", maxWidth:480, maxHeight:"85vh", display:"flex", flexDirection:"column", background:"var(--white)", borderRadius: behind ? 26 : "24px 24px 0 0", borderTop:"1px solid var(--border-strong)", fontFamily:"'Plus Jakarta Sans',sans-serif",
        transform: `translateX(-50%) ${behind ? "translateY(-10px) scale(0.93)" : ""}`,
        filter: behind ? "brightness(0.82)" : "none",
        transition: prefersReducedMotion() ? "none" : "transform .42s cubic-bezier(.2,.8,.2,1), filter .42s ease, border-radius .42s ease"}}>
        <div style={{width:38, height:4, borderRadius:2, background:"rgba(31,27,22,0.18)", margin:"10px auto 6px", flexShrink:0}}/>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 18px 12px", borderBottom:"1px solid var(--border)", flexShrink:0}}>
          <div style={{minWidth:0}}>
            <div style={{fontSize:18, fontWeight:800, color:"var(--ink)"}}>{book}</div>
            <div style={{fontSize:12, color:"var(--stone)"}}>
              {sermonCount.toLocaleString()} sermon{sermonCount===1?"":"s"}{rows ? ` · ${rows.length} verse${rows.length===1?"":"s"} preached` : ""}
            </div>
          </div>
          <button onClick={onClose} style={{background:"none", border:"none", color:"var(--stone)", fontSize:22, cursor:"pointer", padding:4}}>✕</button>
        </div>
        <div style={{flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch", padding:"12px 16px max(18px, env(safe-area-inset-bottom))"}}>
          {rows !== null && (
            <>
              <div style={{display:"flex", gap:10, overflowX:"auto", paddingBottom:6, marginBottom:16}}>
                <StatCard label={`Of ${book}`} value={`${versesPreached}`} sub="verses preached"/>
                <StatCard label="Sermons" value={String(sermonCount)}/>
                <StatCard label="Most preached ch." value={mostPreachedChapter ? `${book} ${mostPreachedChapter}` : "—"}/>
              </div>
              <ChapterGrid chapterCoverage={chapterCoverage} chapterCount={chapterCount} mostPreachedChapter={mostPreachedChapter}
                onChapter={(ch) => onOpenVerse?.(book, ch, 1)}/>
              <HeatLegend/>
              <div style={{fontSize:12.5, color:"var(--stone)", margin:"0 0 16px"}}>Tap a chapter to read it, with its sermons marked.</div>

              <Section title={`Sermons on ${book}`}>
                {sermonRows === null ? <Loading/> : sermonRows.length === 0 ? <Empty>No sermons on {book} yet.</Empty> : (
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    {sermonRows.map(s => {
                      const c = churchById.get(s.church_id);
                      const when = s.date ? new Date(s.date + "T00:00:00").toLocaleDateString("en-GB", { day:"numeric", month:"short" }) : "";
                      return (
                        // Opens the sermon itself, stacked over this book sheet
                        // (docs/FIX-SERMON-CLIPS.md §3d) — this used to call
                        // onOpenVerse(book, 1, 1), which didn't open the sermon
                        // at all, just navigated to chapter 1 verse 1.
                        <button key={s.id} type="button" onClick={() => onOpenSermon?.(s, { behindKind: "book", backLabel: book })}
                          style={{display:"flex", alignItems:"center", gap:10, width:"100%", textAlign:"left", background:"none", border:"none", padding:0, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                          <div style={{flexShrink:0, width:64, height:36, borderRadius:8, overflow:"hidden", background:"var(--border)"}}>
                            {s.youtube_id && <img src={`https://i.ytimg.com/vi/${s.youtube_id}/mqdefault.jpg`} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>}
                          </div>
                          <div style={{flex:1, minWidth:0}}>
                            <div style={{fontSize:11.5, fontWeight:700, color:"var(--lavender-ink)"}}>{c?.name || "Church"}{when ? ` · ${when}` : ""}</div>
                            {/* display_title → "Sunday sermon" — never the raw
                                YouTube title (docs/FIX-SERMON-CLIPS.md §3d). */}
                            <div style={{fontSize:14, fontWeight:700, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{s.display_title || "Sunday sermon"}</div>
                            {s.preacher && <div style={{fontSize:12, color:"var(--stone)"}}>{s.preacher}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Section>
            </>
          )}
          {rows === null ? <Loading/> :
           rows.length === 0 ? null :
           chapters.map(([ch, verses]) => (
             <div key={ch} style={{marginBottom:14}}>
               <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase", color:"var(--stone)", marginBottom:6}}>{book} {ch}</div>
               <div style={{display:"flex", flexDirection:"column", gap:6}}>
                 {verses.map(v => {
                   const avatars = (v.church_ids || []).slice(0, 5);
                   const extra = (v.church_ids?.length || 0) - avatars.length;
                   return (
                     <button key={`${ch}:${v.verse}`} type="button" onClick={()=>onOpenVerse?.(book, ch, v.verse)}
                       style={{display:"flex", alignItems:"flex-start", gap:10, width:"100%", textAlign:"left", background:"var(--white)", border:"1px solid var(--border)", borderRadius:10, padding:"10px 12px", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                       <div style={{flex:1, minWidth:0}}>
                         <div style={{fontSize:14, fontWeight:700, color:"var(--ink)"}}>{book} {ch}:{v.verse}</div>
                         {verseTextMap[`${ch}:${v.verse}`] && (
                           <div style={{fontSize:12.5, fontStyle:"italic", color:"var(--stone)", lineHeight:1.4, marginTop:4, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden"}}>
                             {verseTextMap[`${ch}:${v.verse}`]}
                           </div>
                         )}
                         <div style={{display:"flex", alignItems:"center", marginTop:6}}>
                           {avatars.map((cid, j) => { const c = churchById.get(cid); return (
                             <span key={cid} style={{marginLeft:j===0?0:-6, border:"1.5px solid var(--white)", borderRadius:7}}><ChurchLogo url={c?.logo_url} name={c?.name} size={20}/></span>
                           ); })}
                           {extra > 0 && <span style={{marginLeft:6, fontSize:11, color:"var(--stone)"}}>+{extra}</span>}
                         </div>
                       </div>
                       <span style={{flexShrink:0, marginTop:1, fontSize:11, fontWeight:700, color:"var(--gold-ink)", background:"rgba(232,200,122,0.12)", border:"1px solid rgba(232,200,122,0.25)", borderRadius:8, padding:"3px 8px", whiteSpace:"nowrap"}}>
                         {Number(v.sermon_count)} sermon{Number(v.sermon_count)===1?"":"s"}
                       </span>
                     </button>
                   );
                 })}
               </div>
             </div>
           ))}
        </div>
      </div>
    </>
  );
}

// ── tiny helpers ─────────────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <div style={{marginBottom:22}}>
      <div style={{fontSize:11, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:"var(--stone)"}}>{title}</div>
      {subtitle && <div style={{fontSize:12, color:"var(--stone)", margin:"2px 0 10px", lineHeight:1.4}}>{subtitle}</div>}
      {!subtitle && <div style={{height:10}}/>}
      {children}
    </div>
  );
}
function StatCard({ label, value, sub }) {
  return (
    <div style={{flexShrink:0, minWidth:130, background:"var(--white)", border:"1px solid var(--border)", borderRadius:12, padding:"12px 14px"}}>
      <div style={{fontSize:11, color:"var(--stone)", textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:700}}>{label}</div>
      <div style={{fontSize:18, fontWeight:800, color:"var(--gold-ink)", marginTop:4, lineHeight:1.2}}>{value}</div>
      {sub && <div style={{fontSize:11, color:"var(--stone)", marginTop:1}}>{sub}</div>}
    </div>
  );
}
function Loading() {
  return <div style={{padding:"30px 0", textAlign:"center", fontSize:13, color:"var(--stone)"}}>Loading…</div>;
}
function Empty({ children }) {
  return <div style={{padding:"18px 14px", textAlign:"center", fontSize:13, color:"var(--stone)", background:"rgba(31,27,22,0.03)", border:"1px solid var(--border)", borderRadius:10, lineHeight:1.5}}>{children}</div>;
}
